import { Button, cn } from "@qn/ui";
import {
  ArrowUp,
  AudioLines,
  Bot,
  Brain,
  ClipboardList,
  FileText,
  Image as ImageIcon,
  Mic,
  Paperclip,
  Square,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useFeature } from "../lib/features";
import { useT } from "../lib/i18n";
import { usePrefs } from "../lib/prefs";
import { useSettingsQuery } from "../lib/settings";
import { matchSlash, slashTemplate } from "../lib/slash";
import { type UploadedFile, uploadFile } from "../lib/upload";
import {
  BrowserListener,
  browserLang,
  micSupported,
  openMic,
  Recorder,
  transcribeServer,
  type VoiceSettings,
} from "../lib/voice";
import type { Key } from "../locales/en";

export function Composer({
  running,
  disabled,
  onSend,
  onStop,
  canThink,
  think,
  onThink,
  agent,
  onAgent,
  plan,
  onPlan,
  runnerOnline,
  voice,
  onVoiceMode,
  prefill,
  autoFocusKey,
}: {
  running: boolean;
  disabled?: boolean;
  onSend: (text: string, attachments: string[]) => void;
  onStop: () => void;
  canThink: boolean;
  think: boolean;
  onThink: (v: boolean) => void;
  agent: boolean;
  onAgent: (v: boolean) => void;
  plan: boolean;
  onPlan: (v: boolean) => void;
  runnerOnline: boolean | undefined;
  voice: VoiceSettings;
  onVoiceMode: () => void;
  prefill?: { text: string; nonce: number } | null;
  autoFocusKey?: string;
}) {
  const t = useT();
  const [text, setText] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [imageMode, setImageMode] = useState(false);
  const canUpload = useFeature("uploads");
  const canVoice = useFeature("voice");
  const suggestionsOn = useFeature("suggestions");
  const readAloud = usePrefs((st) => st.readAloud);
  const setPrefs = usePrefs((st) => st.set);
  const canAgent = useFeature("agent");
  const canImage = useFeature("images");
  const locale = usePrefs((s) => s.locale);
  const sendOnEnter = usePrefs((s) => s.sendOnEnter);
  const prefsSettings = useSettingsQuery(true).data?.settings;
  const completeOn = suggestionsOn && prefsSettings?.suggestAutocomplete !== false;
  const aiOn = suggestionsOn && prefsSettings?.suggestAi !== false;
  const [sugg, setSugg] = useState<Array<{ text: string; ai?: boolean; cmd?: string }>>([]);
  const [active, setActive] = useState(-1);
  const reqId = useRef(0);
  // Google-style completion: your own past prompts instantly, plus (optionally) a free AI model's guesses.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run when the text or the options change
  useEffect(() => {
    const q = text.split("\n")[0] ?? "";
    const id = ++reqId.current;
    // "/…" shows prompt templates instead of history suggestions.
    const slash = matchSlash(text);
    if (slash.length) {
      setSugg(slash.map((c) => ({ text: slashTemplate(c, locale), cmd: c.cmd })));
      setActive(0);
      return;
    }
    if (!completeOn || running || text.includes("\n") || q.trim().length < 2) {
      setSugg([]);
      return;
    }
    const h = setTimeout(async () => {
      try {
        const r = await api<{ items: string[] }>(`/suggest/complete?q=${encodeURIComponent(q)}`);
        if (id !== reqId.current) return;
        setSugg(r.items.map((x) => ({ text: x })));
        setActive(-1);
      } catch {
        /* suggestions are a convenience only */
      }
    }, 150);
    const ai =
      aiOn && q.trim().length >= 6
        ? setTimeout(async () => {
            try {
              const r = await api<{ items: string[] }>("/suggest/ai", {
                method: "POST",
                body: { q },
              });
              if (id !== reqId.current) return;
              setSugg((cur) => [
                ...cur,
                ...r.items
                  .filter((x) => !cur.some((c) => c.text === x))
                  .map((x) => ({ text: x, ai: true })),
              ]);
            } catch {
              /* ignore */
            }
          }, 500)
        : undefined;
    return () => {
      clearTimeout(h);
      if (ai) clearTimeout(ai);
    };
  }, [text, completeOn, aiOn, running, locale]);
  const accept = (s: string) => {
    setText(s);
    setSugg([]);
    setActive(-1);
    reqId.current++;
    ref.current?.focus();
  };
  const [dictating, setDictating] = useState<"idle" | "rec" | "busy">("idle");
  const dictation = useRef<{ stop: () => Promise<string> } | null>(null);
  const toggleDictation = async () => {
    setUploadError(null);
    if (dictation.current) {
      setDictating("busy");
      const h = dictation.current;
      dictation.current = null;
      try {
        const said = await h.stop();
        if (said) setText((cur) => (cur ? `${cur} ${said}` : said));
      } catch (e) {
        setUploadError(e instanceof Error ? e.message : String(e));
      }
      setDictating("idle");
      ref.current?.focus();
      return;
    }
    try {
      if (voice.stt.source === "browser") {
        const l = new BrowserListener();
        if (!l.start(browserLang(voice.lang, locale))) throw new Error(t("voice.unsupported"));
        dictation.current = { stop: () => l.stop() };
      } else {
        const ms = await openMic();
        const r = new Recorder(ms);
        r.start();
        dictation.current = {
          stop: async () => {
            const blob = await r.stop();
            for (const x of ms.getTracks()) x.stop();
            return blob.size < 1500 ? "" : transcribeServer(blob, voice.lang);
          },
        };
      }
      setDictating("rec");
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : t("voice.noMic"));
    }
  };
  const addFiles = async (list: FileList | File[]) => {
    setUploadError(null);
    for (const f of Array.from(list).slice(0, 8)) {
      setUploading((n) => n + 1);
      try {
        const up = await uploadFile(f);
        setFiles((cur) => (cur.length >= 8 ? cur : [...cur, up]));
      } catch (e) {
        setUploadError(e instanceof Error ? e.message : String(e));
      } finally {
        setUploading((n) => n - 1);
      }
    }
  };

  // Drop a file anywhere on the page (not only on the box): full-screen hint while dragging.
  const addRef = useRef(addFiles);
  addRef.current = addFiles;
  useEffect(() => {
    let depth = 0;
    const hasFiles = (e: DragEvent) => !!e.dataTransfer?.types.includes("Files");
    const enter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth++;
      setDragging(true);
    };
    const over = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    };
    const leave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setDragging(false);
    };
    const drop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      setDragging(false);
      if (e.dataTransfer?.files.length) void addRef.current(e.dataTransfer.files);
    };
    addEventListener("dragenter", enter);
    addEventListener("dragover", over);
    addEventListener("dragleave", leave);
    addEventListener("drop", drop);
    return () => {
      removeEventListener("dragenter", enter);
      removeEventListener("dragover", over);
      removeEventListener("dragleave", leave);
      removeEventListener("drop", drop);
    };
  }, []);

  // Draft autosave: what you typed survives a refresh or switching chats, per conversation.
  const draftKey = `qn-draft:${autoFocusKey ?? "new"}`;
  // biome-ignore lint/correctness/useExhaustiveDependencies: load once per conversation
  useEffect(() => {
    try {
      setText(localStorage.getItem(draftKey) ?? "");
    } catch {
      /* private mode */
    }
  }, [draftKey]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: save when the text changes
  useEffect(() => {
    const h = setTimeout(() => {
      try {
        if (text.trim()) localStorage.setItem(draftKey, text);
        else localStorage.removeItem(draftKey);
      } catch {
        /* private mode */
      }
    }, 400);
    return () => clearTimeout(h);
  }, [text]);

  useEffect(() => {
    if (prefill) {
      setText(prefill.text);
      ref.current?.focus();
    }
  }, [prefill]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: refocus when the conversation changes
  useEffect(() => {
    ref.current?.focus();
  }, [autoFocusKey]);
  // Auto-grow up to ~40% of the viewport.
  // biome-ignore lint/correctness/useExhaustiveDependencies: resize whenever the text changes
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, window.innerHeight * 0.4)}px`;
  }, [text]);

  const send = () => {
    const v = text.trim() || (files.length ? t("composer.lookAttached") : "");
    if (!v || running || disabled || uploading) return;
    onSend(
      imageMode ? `/image ${v}` : v,
      files.map((f) => f.id),
    );
    setImageMode(false);
    setText("");
    setFiles([]);
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (sugg.length && !e.nativeEvent.isComposing) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const n = sugg.length;
        setActive((a) => (e.key === "ArrowDown" ? (a + 1) % n : (a - 1 + n) % n));
        return;
      }
      if (e.key === "Tab" && !e.shiftKey) {
        e.preventDefault();
        accept((sugg[active >= 0 ? active : 0] as { text: string }).text);
        return;
      }
      if (e.key === "Enter" && active >= 0) {
        e.preventDefault();
        accept((sugg[active] as { text: string }).text);
        return;
      }
      if (e.key === "Escape") {
        setSugg([]);
        return;
      }
    }
    // Don't send while an IME (Arabic/CJK) composition is active.
    if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
    const mod = e.ctrlKey || e.metaKey;
    const touch = !window.matchMedia("(pointer: fine)").matches;
    if (sendOnEnter ? !e.shiftKey && !touch : mod) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="mx-auto w-full max-w-[var(--chat-width)] px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-4">
      {dragging && (
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 z-[80] grid place-items-center bg-[var(--bg)]/80 backdrop-blur-sm"
        >
          <div className="rounded-3xl border-2 border-dashed border-[var(--accent)] bg-[var(--accent-soft)] px-10 py-8 text-center">
            <p className="text-lg font-semibold">{t("upload.drop")}</p>
            <p className="mt-1 text-sm text-[var(--fg-muted)]">{t("upload.dropHint")}</p>
          </div>
        </div>
      )}
      <div
        className={cn(
          "glass rounded-2xl border border-[var(--line-strong)] p-2 shadow-[var(--shadow)] transition focus-within:border-[var(--accent)] focus-within:ring-2 focus-within:ring-[var(--accent-ring)]",
          dragging && "border-[var(--accent)] bg-[var(--accent-soft)]",
        )}
      >
        {(files.length > 0 || uploading > 0 || uploadError) && (
          <div className="flex flex-wrap gap-1.5 px-1.5 pb-1.5 pt-0.5">
            {files.map((f) => (
              <span
                key={f.id}
                className="flex max-w-56 items-center gap-1.5 rounded-lg bg-[var(--surface-3)] py-1 ps-1.5 pe-1 text-xs"
              >
                {f.kind === "image" ? (
                  <img
                    src={`/api/files/${f.id}?inline=1`}
                    alt=""
                    className="size-6 rounded object-cover"
                  />
                ) : (
                  <FileText className="size-4 shrink-0 text-[var(--fg-muted)]" />
                )}
                <span className="truncate" dir="auto">
                  {f.name}
                </span>
                <button
                  type="button"
                  aria-label={t("action.delete")}
                  onClick={() => setFiles((c) => c.filter((x) => x.id !== f.id))}
                  className="grid size-5 place-items-center rounded hover:bg-[var(--line)]"
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
            {uploading > 0 && (
              <span className="rounded-lg bg-[var(--surface-3)] px-2 py-1 text-xs text-[var(--fg-muted)]">
                {t("composer.uploading")}
              </span>
            )}
            {uploadError && (
              <span
                role="alert"
                className="rounded-lg bg-[color-mix(in_oklab,var(--danger)_12%,transparent)] px-2 py-1 text-xs text-[var(--danger)]"
              >
                {uploadError}
              </span>
            )}
          </div>
        )}
        {sugg.length > 0 && (
          <div
            role="listbox"
            aria-label={t("suggest.listLabel")}
            className="mx-1 mb-1 overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--surface-2)] text-sm shadow-sm"
          >
            {sugg.map((x, i) => (
              <div key={x.text} role="option" tabIndex={-1} aria-selected={i === active}>
                <button
                  type="button"
                  dir="auto"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    accept(x.text);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 px-3 py-1.5 text-start hover:bg-[var(--surface-3)]",
                    i === active && "bg-[var(--surface-3)]",
                  )}
                >
                  {x.cmd ? (
                    <span className="min-w-0 flex-1 truncate" dir="auto">
                      <span className="font-mono font-medium" dir="ltr">
                        /{x.cmd}
                      </span>{" "}
                      <span className="text-[var(--fg-faint)]">{t(`slash.${x.cmd}` as Key)}</span>
                    </span>
                  ) : (
                    <span className="min-w-0 flex-1 truncate">
                      <span className="text-[var(--fg-faint)]">{x.text.slice(0, text.length)}</span>
                      <span className="font-medium">{x.text.slice(text.length)}</span>
                    </span>
                  )}
                  {x.ai && (
                    <span className="text-[10px] uppercase text-[var(--accent-text)]">AI</span>
                  )}
                </button>
              </div>
            ))}
            <div className="border-t border-[var(--line)] px-3 py-1 text-[10px] text-[var(--fg-faint)]">
              {t("suggest.hint")}
            </div>
          </div>
        )}
        <textarea
          ref={ref}
          dir="auto"
          rows={1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          onPaste={(e) => {
            const imgs = Array.from(e.clipboardData.files).filter((f) =>
              f.type.startsWith("image/"),
            );
            if (imgs.length) {
              e.preventDefault();
              void addFiles(imgs);
            }
          }}
          placeholder={t("composer.placeholder")}
          aria-label={t("composer.placeholder")}
          className="max-h-[40dvh] w-full resize-none bg-transparent px-2.5 py-2 text-[0.97rem] leading-relaxed outline-none placeholder:text-[var(--fg-faint)]"
        />
        <div className="flex items-center justify-between gap-2 px-1 pt-1">
          <div className="flex items-center gap-1">
            <input
              ref={fileInput}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                if (e.target.files) void addFiles(e.target.files);
                e.target.value = "";
              }}
            />
            {canUpload && (
              <button
                type="button"
                aria-label={t("composer.attach")}
                title={t("composer.attach")}
                onClick={() => fileInput.current?.click()}
                className="grid size-8 place-items-center rounded-lg text-[var(--fg-muted)] transition hover:bg-[var(--surface-3)]"
              >
                <Paperclip className="size-4" />
              </button>
            )}
            {canVoice && micSupported() && (
              <>
                <button
                  type="button"
                  aria-label={dictating === "rec" ? t("voice.stopDictation") : t("voice.dictate")}
                  title={dictating === "rec" ? t("voice.stopDictation") : t("voice.dictate")}
                  aria-pressed={dictating === "rec"}
                  disabled={dictating === "busy"}
                  onClick={() => void toggleDictation()}
                  className={cn(
                    "grid size-8 place-items-center rounded-lg transition hover:bg-[var(--surface-3)]",
                    dictating === "rec"
                      ? "animate-pulse bg-[color-mix(in_oklab,var(--danger)_18%,transparent)] text-[var(--danger)]"
                      : "text-[var(--fg-muted)]",
                  )}
                >
                  <Mic className="size-4" />
                </button>
                <button
                  type="button"
                  aria-label={t("voice.title")}
                  title={t("voice.title")}
                  onClick={onVoiceMode}
                  disabled={disabled}
                  className="grid size-8 place-items-center rounded-lg text-[var(--fg-muted)] transition hover:bg-[var(--surface-3)]"
                >
                  <AudioLines className="size-4" />
                </button>
              </>
            )}
            {canVoice && (
              <button
                type="button"
                aria-pressed={readAloud}
                aria-label={t("voice.readAloud")}
                title={t("voice.readAloud")}
                onClick={() => setPrefs({ readAloud: !readAloud })}
                className={cn(
                  "grid size-8 place-items-center rounded-lg transition hover:bg-[var(--surface-3)]",
                  readAloud
                    ? "bg-[var(--accent-soft)] text-[var(--accent-text)]"
                    : "text-[var(--fg-muted)]",
                )}
              >
                {readAloud ? <Volume2 className="size-4" /> : <VolumeX className="size-4" />}
              </button>
            )}
            {canImage && (
              <button
                type="button"
                aria-pressed={imageMode}
                onClick={() => setImageMode((v) => !v)}
                className={cn(
                  "flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition",
                  imageMode
                    ? "bg-[var(--accent-soft)] text-[var(--accent-text)]"
                    : "text-[var(--fg-muted)] hover:bg-[var(--surface-3)]",
                )}
              >
                <ImageIcon className="size-4" /> {t("composer.image")}
              </button>
            )}
            {canAgent && (
              <button
                type="button"
                aria-pressed={agent}
                onClick={() => onAgent(!agent)}
                className={cn(
                  "flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition",
                  agent
                    ? "bg-[var(--accent-soft)] text-[var(--accent-text)]"
                    : "text-[var(--fg-muted)] hover:bg-[var(--surface-3)]",
                )}
              >
                <Bot className="size-4" /> {t("composer.agent")}
              </button>
            )}
            {agent && (
              <button
                type="button"
                aria-pressed={plan}
                onClick={() => onPlan(!plan)}
                title={t("composer.planHint")}
                className={cn(
                  "flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition",
                  plan
                    ? "bg-[var(--accent-soft)] text-[var(--accent-text)]"
                    : "text-[var(--fg-muted)] hover:bg-[var(--surface-3)]",
                )}
              >
                <ClipboardList className="size-4" /> {t("composer.plan")}
              </button>
            )}
            {agent && runnerOnline === false && (
              <Link
                to="/settings/runner"
                className="rounded-lg px-2 py-1.5 text-[11px] text-[var(--warn)] hover:bg-[var(--surface-3)]"
                title={t("agent.sandboxOfflineHint")}
              >
                {t("agent.sandboxOffline")}
              </Link>
            )}
            {canThink && (
              <button
                type="button"
                aria-pressed={think}
                onClick={() => onThink(!think)}
                className={cn(
                  "flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition",
                  think
                    ? "bg-[var(--accent-soft)] text-[var(--accent-text)]"
                    : "text-[var(--fg-muted)] hover:bg-[var(--surface-3)]",
                )}
              >
                <Brain className="size-4" /> {t("composer.think")}
              </button>
            )}
          </div>
          {running ? (
            <Button
              size="icon"
              variant="secondary"
              onClick={onStop}
              aria-label={t("composer.stop")}
            >
              <Square className="size-4 fill-current" />
            </Button>
          ) : (
            <Button
              size="icon"
              variant="primary"
              onClick={send}
              disabled={(!text.trim() && !files.length) || disabled || uploading > 0}
              aria-label={t("composer.send")}
            >
              <ArrowUp className="size-5 rtl:-scale-x-100 rtl:rotate-0" />
            </Button>
          )}
        </div>
      </div>
      <p className="mt-2 text-center text-[11px] text-[var(--fg-faint)]">
        {t("composer.disclaimer")}
      </p>
    </div>
  );
}
